import {
  AgGridReact,
  type CustomCellRendererProps,
} from '@mint-lib/ag-grid/react';
import {
  ComponentProps,
  memo,
  Ref,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  CellStyleModule,
  ColumnAutoSizeModule,
  CustomFilterModule,
  ModuleRegistry,
  PaginationModule,
  RowStyleModule,
  TextFilterModule,
  ValidationModule,
  type ColDef,
  type GetContextMenuItems,
  type GridApi,
  type IServerSideDatasource,
  type ValueFormatterParams,
} from '@mint-lib/ag-grid/community';
import {
  ColumnApiModule,
  ColumnMenuModule,
  ContextMenuModule,
  FiltersToolPanelModule,
  LicenseManager,
  ServerSideRowModelApiModule,
  ServerSideRowModelModule,
  SetFilterModule,
  SideBarModule,
} from '@mint-lib/ag-grid/enterprise';
import { startOperation } from '@mint-lib/datadog';
import { useTranslation, type TFunction } from '@mint-lib/i18n';
import { Copy, Delete, Package, User } from '@mint-lib/icons';
import { enqueueSnackbar } from '@mint-lib/notifications';
import { useQueryClient } from '@mint-lib/query-client';
import {
  Button,
  Divider,
  TruncateText,
  typographyVariants,
} from '@mint-lib/tw-components';
import { StaticDateSpan } from '@mint-lib/tw-components/patterns';
import { cn } from '@mint-lib/tw-utils';

import type {
  GranularityLevel,
  PlatformsByName,
  TemplateListItem,
  TemplateListItemV1,
  TemplateListItemV2,
} from '../../api/types.js';
import { useLocalizedGranularityLevelLabels } from '../../constants/i18n/granularityLevel.js';
import {
  useImportHubV2,
  useInfiniteScrollFF,
  usePlatformCampaignLevelFF,
} from '../../hooks/ff/index.js';
import { useIsReadOnlyUser, useShouldNeverHappen } from '../../hooks/index.js';
import {
  useImportHubPaths,
  usePerformanceRouteApi,
} from '../../hooks/useImportHubRouteApi.js';
import {
  usePlatformsCatalog,
  useTemplateFilterOptionsQueryOptions,
  useTemplatesListQueryOptions,
} from '../../queries/index.js';
import {
  convertFilterModelToURLParams,
  createTemplatesFilterModelFromUrlParams,
  transformFilterModelToAPIParams,
  type FilterModel,
} from '../../utils/ag-grid.js';
import { checkIsNewItem, getRowClassName } from '../../utils/dataFileUtils.jsx';
import { GridDateRangeInput } from '../atoms/GridDateRangeInput.jsx';
import { GridDateRangePicker } from '../atoms/GridDateRangePicker.jsx';
import { GridEmptyState } from '../atoms/GridEmptyState.jsx';
import { GridMenuItem } from '../atoms/GridMenuItem.jsx';
import { Tooltip } from '../atoms/Tooltip.jsx';
import { CollaboratorsList } from './CollaboratorsList.jsx';
import { GridTemplateMoreActions } from './GridTemplateMoreActions.jsx';
import PlatformsList from './PlatformsList.jsx';
import { TemplateCollaboratorsDialog } from './TemplateCollaboratorsDialog.jsx';
import { TemplateDeleteDialog } from './TemplateDeleteDialog.jsx';
import { TemplateDuplicateDrawer } from './TemplateDuplicationDrawer/index.js';
import { TemplatePreviewDrawer } from './TemplatePreviewDrawer.jsx';

type AGGridProps = ComponentProps<typeof AgGridReact<TemplateListItem>>;
type SetFilterValuesParams = {
  success: (values: string[]) => void;
};

ModuleRegistry.registerModules([
  ServerSideRowModelModule,
  RowStyleModule,
  CellStyleModule,
  ColumnApiModule,
  ColumnMenuModule,
  ContextMenuModule,
  PaginationModule,
  ValidationModule,
  ColumnAutoSizeModule,
  ServerSideRowModelApiModule,
  SideBarModule,
  TextFilterModule,
  SetFilterModule,
  CustomFilterModule,
  FiltersToolPanelModule,
]);

if (import.meta.env.VITE_AG_GRID_LICENSE_KEY) {
  LicenseManager.setLicenseKey(import.meta.env.VITE_AG_GRID_LICENSE_KEY);
}

const PLACEHOLDER_ROWS_COUNT = 20;

const getTemplateTableTranslations = (t: TFunction) => ({
  activity: t('Activity'),
  attributionLevel: t('Attribution level'),
  campaign: t('Campaign'),
  creationDate: t('Creation date'),
  involvedPlatforms: t('Involved platforms'),
  mailForAutoImport: t('Mail for auto-import'),
  owner: t('Owner'),
  templateName: t('Template name'),
});

const getTemplateContextMenuTranslations = (t: TFunction) => ({
  addCollaborators: t('Add collaborators'),
  delete: t('Delete'),
  duplicate: t('Duplicate'),
  importWithThisTemplate: t('Import with this template'),
});

type TemplatesTableProps = {
  ref: Ref<{ onSearchChange: (searchQuery: string) => void } | null>;
};

export const TemplatesTable = ({ ref }: TemplatesTableProps) => {
  const { t } = useTranslation('@myn/import-hub');
  const performanceRoute = usePerformanceRouteApi();
  const routeSearch = performanceRoute.useSearch();
  const { search, filters: filterParams = {}, itemsPerPage } = routeSearch;
  const navigate = performanceRoute.useNavigate();
  const paths = useImportHubPaths();
  const isInfiniteScroll = useInfiniteScrollFF();
  const queryClient = useQueryClient();
  const gridRef = useRef<AgGridReact>(null);
  const shouldNeverHappen = useShouldNeverHappen();
  const [forcedRenderError, setForcedRenderError] = useState<null | Error>(
    null,
  );

  const setSearchFilter = useCallback((searchQuery: string) => {
    if (!gridRef.current?.api) {
      return;
    }

    gridRef.current.api.setColumnFilterModel('search', {
      field: 'search',
      filterType: 'text',
      filter: searchQuery,
      type: 'contains',
    });
    gridRef.current.api.onFilterChanged();
  }, []);

  useImperativeHandle(ref, () => ({
    onSearchChange: setSearchFilter,
  }));

  const isImportHubV2 = useImportHubV2();
  const isPlatformCampaignLevelEnabled = usePlatformCampaignLevelFF();
  const isReadOnlyUser = useIsReadOnlyUser();
  const {
    data: { platformsByName },
  } = usePlatformsCatalog();
  const granularityLevelLabelByType = useLocalizedGranularityLevelLabels();
  const getTemplateFilterOptionsQueryOptions =
    useTemplateFilterOptionsQueryOptions();

  const [copiedEmail, setCopiedEmail] = useState<string | null>(null);

  const copyToClipboard = useCallback(
    async (text: string) => {
      await navigator.clipboard.writeText(text);
      setCopiedEmail(text);
      enqueueSnackbar({
        variant: 'success',
        title: t('Mail copied!'),
        removable: true,
      });
    },
    [t],
  );

  const filterOptionsCache = useRef<Record<string, Record<string, string>>>({});

  const getFilterOptions = useCallback(
    (filterType: string) => async (params: SetFilterValuesParams) => {
      const filterOptionsQueryOptions = getTemplateFilterOptionsQueryOptions({
        params: {
          include: [filterType],
        },
      });

      const { options } = await queryClient.ensureQueryData(
        filterOptionsQueryOptions,
      );

      filterOptionsCache.current[filterType] = Object.fromEntries(
        options.map((opt) => [opt.key, opt.value]),
      );

      params.success(options.map((opt) => opt.key));
    },
    [queryClient, getTemplateFilterOptionsQueryOptions],
  );

  const getFilterValueFormatter = useCallback(
    (filterType: string) =>
      (params: ValueFormatterParams<TemplateListItem>) => {
        return filterOptionsCache.current[filterType][params.value];
      },
    [],
  );

  const getTemplatesListQueryOptions = useTemplatesListQueryOptions();

  const serverSideDataSource: IServerSideDatasource = useMemo(() => {
    return {
      getRows: async (params) => {
        params.api.hideOverlay();

        const { startRow, endRow } = params.request;
        const filterModel = params.request.filterModel as FilterModel;
        if (startRow === undefined || endRow === undefined) {
          shouldNeverHappen('Ag-grid: startRow or endRow is not defined');
          return params.fail();
        }

        const itemsPerPage = endRow - startRow;
        const pageNumber = Math.floor(startRow / itemsPerPage) + 1;

        const filters = transformFilterModelToAPIParams(filterModel);

        try {
          const templatesQueryOptions = getTemplatesListQueryOptions({
            params: {
              pageNumber,
              itemsPerPage,
              ...filters,
            },
          });

          const data = await queryClient.fetchQuery({
            ...templatesQueryOptions,
            staleTime: 30 * 1000,
          });

          if (!data.totalItems) {
            params.api.showNoRowsOverlay();
          }

          params.success({
            rowData: data.items,
            rowCount: data.totalItems,
          });
        } catch (error) {
          setForcedRenderError(error);
        }
      },
    };
  }, [queryClient, shouldNeverHappen, getTemplatesListQueryOptions]);

  const [templateToPreview, setTemplateToPreview] =
    useState<TemplateListItem | null>(null);
  const [templateToDuplicate, setTemplateToDuplicate] =
    useState<TemplateListItem | null>(null);
  const [templateToManageCollaborators, setTemplateToManageCollaborators] =
    useState<TemplateListItem | null>(null);
  const [templateToDelete, setTemplateToDelete] =
    useState<TemplateListItem | null>(null);

  const handlePreviewTemplateClick = useCallback(
    (template: TemplateListItem) => {
      setTemplateToPreview(template);
    },
    [],
  );

  const handleDuplicateTemplateClick = useCallback(
    (template: TemplateListItem) => {
      setTemplateToDuplicate(template);
    },
    [],
  );

  const handleManageCollaboratorsClick = useCallback(
    (template: TemplateListItem) => {
      setTemplateToManageCollaborators(template);
    },
    [],
  );

  const handleDeleteTemplateClick = useCallback(
    (template: TemplateListItem) => {
      setTemplateToDelete(template);
    },
    [],
  );

  const handleImportWithTemplateClick = useCallback(
    (template: TemplateListItem) => {
      // Ended by useImportOperation in the data file wizard.
      startOperation('import-hub.import');
      navigate({
        to: paths.importsNew,
        search: { template: template.id },
      });
    },
    [navigate, paths.importsNew],
  );

  const defaultColDef = useMemo(
    (): ColDef => ({
      sortable: false,
      resizable: true,
      suppressHeaderMenuButton: true,
      cellClass: '!flex !justify-start !items-center',
    }),
    [],
  );

  const templateTableTranslations = useMemo(
    () => getTemplateTableTranslations(t),
    [t],
  );

  const columnDefs = useMemo(() => {
    const attributionLevelFilterValues = [
      'pricing_item',
      'platform',
      'media_row',
      ...(isPlatformCampaignLevelEnabled ? ['platform_campaign'] : []),
    ];

    const columns: Array<ColDef> = [
      {
        field: 'search',
        headerName: '',
        hide: true,
        filter: 'agTextColumnFilter',
        cellDataType: false,
      },
      {
        headerName: templateTableTranslations.templateName,
        field: 'name',
        cellRenderer: TemplateNameRenderer,
        cellRendererParams: { t, handlePreviewTemplateClick },
        minWidth: 300,
      },
      {
        headerName: templateTableTranslations.mailForAutoImport,
        field: 'email',
        cellRenderer: EmailRenderer,
        cellRendererParams: {
          t,
          copyToClipboard,
          copiedEmail,
          isReadOnlyUser,
        },
        filter: 'agSetColumnFilter',
        filterParams: {
          values: getFilterOptions('email'),
          valueFormatter: getFilterValueFormatter('email'),
        },
        minWidth: 300,
      },
      {
        headerName: templateTableTranslations.attributionLevel,
        field: 'granularityLevel',
        cellRenderer: GranularityLevelRenderer,
        cellRendererParams: {
          granularityLevelLabelByType: granularityLevelLabelByType,
        },
        filter: 'agSetColumnFilter',
        filterParams: {
          values: attributionLevelFilterValues,
          valueFormatter: (params: ValueFormatterParams) =>
            granularityLevelLabelByType[params.value as GranularityLevel] ||
            params.value,
        },
        minWidth: 180,
      },
      {
        headerName: templateTableTranslations.involvedPlatforms,
        field: 'platform',
        cellRenderer: UnifiedPlatformRenderer,
        cellRendererParams: { platformsByName },
        minWidth: 200,
      },
      {
        field: 'createdDate',
        context: {
          syncWithField: 'createdDateFilterPanel',
        },
        headerName: templateTableTranslations.creationDate,
        cellRenderer: CreationDateRenderer,
        cellRendererParams: { t },
        filter: { component: GridDateRangePicker },
        suppressFiltersToolPanel: true,
        minWidth: 180,
      },
      {
        field: 'createdDateFilterPanel',
        context: {
          syncWithField: 'createdDate',
        },
        headerName: templateTableTranslations.creationDate,
        filter: { component: GridDateRangeInput },
        suppressHeaderMenuButton: false,
        hide: true,
      },
      ...(isImportHubV2
        ? [
            {
              headerName: templateTableTranslations.activity,
              field: 'activity',
              cellRenderer: PlainTextRenderer,
              cellRendererParams: { fieldName: 'activityName' },
              minWidth: 250,
              filter: 'agSetColumnFilter',
              filterParams: {
                values: getFilterOptions('activity'),
                valueFormatter: getFilterValueFormatter('activity'),
              },
            },
          ]
        : [
            {
              headerName: templateTableTranslations.campaign,
              field: 'campaign',
              cellRenderer: PlainTextRenderer,
              cellRendererParams: { fieldName: 'campaign' },
              minWidth: 250,
              filter: 'agSetColumnFilter',
              filterParams: {
                values: getFilterOptions('campaign'),
                valueFormatter: getFilterValueFormatter('campaign'),
              },
            },
          ]),
      {
        headerName: templateTableTranslations.owner,
        field: 'owner',
        cellRenderer: CollaboratorsRenderer,
        minWidth: 200,
        filter: 'agSetColumnFilter',
        filterParams: {
          values: getFilterOptions('owner'),
          valueFormatter: getFilterValueFormatter('owner'),
        },
      },
      ...(isReadOnlyUser
        ? []
        : [
            {
              field: 'actions',
              headerName: '',
              cellRenderer: ActionsRenderer,
              cellRendererParams: {
                handleManageCollaboratorsClick,
                handleDuplicateTemplateClick,
                handleDeleteTemplateClick,
              },
              pinned: 'right' as const,
              resizable: false,
              width: 40,
              minWidth: 40,
              cellClass: '!flex !justify-center !items-center',
            },
          ]),
    ];

    return columns;
  }, [
    t,
    templateTableTranslations,
    platformsByName,
    granularityLevelLabelByType,
    isImportHubV2,
    isPlatformCampaignLevelEnabled,
    isReadOnlyUser,
    handlePreviewTemplateClick,
    copyToClipboard,
    copiedEmail,
    getFilterOptions,
    getFilterValueFormatter,
    handleManageCollaboratorsClick,
    handleDuplicateTemplateClick,
    handleDeleteTemplateClick,
  ]);

  const getRowClass: AGGridProps['getRowClass'] = (params) => {
    const { data } = params;

    if (!data) {
      return '';
    }

    return getRowClassName({ disabled: false, createdAt: data.created });
  };

  const initialFilterModel = useMemo(
    () =>
      createTemplatesFilterModelFromUrlParams({
        search,
        ...filterParams,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const onGridReady: AGGridProps['onGridReady'] = useCallback(
    ({ api }: { api: GridApi }) => {
      // Apply initial filter model
      if (Object.keys(initialFilterModel).length > 0) {
        api.setFilterModel(initialFilterModel);
      }
    },
    [initialFilterModel],
  );

  const onFilterChanged: AGGridProps['onFilterChanged'] = useCallback(
    (params) => {
      if (params.source === 'api') {
        return;
      }

      void (async () => {
        const column = params.columns[0].getColDef();
        const { field, context } = column;

        if (!field) {
          return;
        }

        if (context?.syncWithField) {
          const columnFilterModel = params.api.getColumnFilterModel(field);
          await params.api.setColumnFilterModel(
            context.syncWithField,
            columnFilterModel,
          );
          params.api.onFilterChanged();
        }

        const filterModel = params.api.getFilterModel();
        const filtersParams = convertFilterModelToURLParams(filterModel);

        navigate({
          search: (prev) => ({
            ...prev,
            filters: filtersParams,
          }),
          replace: true,
        });

        params.api.refreshServerSide({ purge: true });
      })();
    },
    [navigate],
  );

  const lastPageSizeRef = useRef(itemsPerPage);
  const onPaginationChanged: AGGridProps['onPaginationChanged'] = useCallback(
    ({ api }: { api: GridApi }) => {
      const newPageSize = api.paginationGetPageSize();
      if (newPageSize === lastPageSizeRef.current) return;
      lastPageSizeRef.current = newPageSize;

      api.setGridOption('cacheBlockSize', newPageSize);

      navigate({
        search: (prev) => ({
          ...prev,
          itemsPerPage: newPageSize,
        }),
        replace: true,
      });
    },
    [navigate],
  );

  const templateContextMenuTranslations = useMemo(
    () => getTemplateContextMenuTranslations(t),
    [t],
  );

  const getContextMenuItems: GetContextMenuItems<TemplateListItem> =
    useCallback(
      (params) => {
        if (!params.node?.data || isReadOnlyUser) {
          return [];
        }

        const { data: template } = params.node;

        return [
          {
            name: 'import-with-template',
            menuItem: GridMenuItem,
            menuItemParams: {
              'data-dd-action-name': 'Template context menu: import data',
              onClick: () => handleImportWithTemplateClick(template),
              children: (
                <>
                  <Package />
                  {templateContextMenuTranslations.importWithThisTemplate}
                </>
              ),
            },
          },
          {
            name: templateContextMenuTranslations.duplicate,
            menuItem: GridMenuItem,
            menuItemParams: {
              onClick: () => handleDuplicateTemplateClick(template),
              children: (
                <>
                  <Copy />
                  {templateContextMenuTranslations.duplicate}
                </>
              ),
            },
          },
          {
            name: 'add-collaborators',
            menuItem: GridMenuItem,
            menuItemParams: {
              onClick: () => handleManageCollaboratorsClick(template),
              children: (
                <>
                  <User />
                  {templateContextMenuTranslations.addCollaborators}
                </>
              ),
            },
          },
          {
            name: 'separator',
            menuItem: Divider,
          },
          {
            name: templateContextMenuTranslations.delete,
            menuItem: GridMenuItem,
            menuItemParams: {
              onClick: () => handleDeleteTemplateClick(template),
              className: 'fill-text-error text-text-error',
              children: (
                <>
                  <Delete />
                  {templateContextMenuTranslations.delete}
                </>
              ),
            },
          },
        ];
      },
      [
        handleDeleteTemplateClick,
        handleDuplicateTemplateClick,
        handleImportWithTemplateClick,
        handleManageCollaboratorsClick,
        isReadOnlyUser,
        templateContextMenuTranslations,
      ],
    );

  const handleTemplateDeleteSuccess = () => {
    gridRef.current?.api.applyServerSideTransaction({
      remove: [templateToDelete],
    });
  };

  const refreshServerSide = () => {
    gridRef.current?.api.refreshServerSide({
      purge: true,
    });
  };

  if (forcedRenderError) {
    throw forcedRenderError;
  }

  const paginationProps = isInfiniteScroll
    ? {
        cacheBlockSize: 50,
        maxBlocksInCache: 10,
      }
    : {
        pagination: true,
        paginationPageSizeSelector: [10, 20, 50, 100],
        paginationPageSize: itemsPerPage,
        cacheBlockSize: itemsPerPage,
        onPaginationChanged,
      };

  return (
    <>
      <MemoizedAgGrid
        ref={gridRef}
        defaultColDef={defaultColDef}
        columnDefs={columnDefs}
        getRowClass={getRowClass}
        serverSideDatasource={serverSideDataSource}
        getContextMenuItems={getContextMenuItems}
        onGridReady={onGridReady}
        onFilterChanged={onFilterChanged}
        {...paginationProps}
      />
      {!!templateToDelete && (
        <TemplateDeleteDialog
          templateId={templateToDelete.id}
          onSuccess={handleTemplateDeleteSuccess}
          onClose={() => setTemplateToDelete(null)}
        />
      )}
      <TemplatePreviewDrawer
        template={templateToPreview}
        onDrawerClose={() => setTemplateToPreview(null)}
      />
      <TemplateDuplicateDrawer
        template={templateToDuplicate}
        onSuccess={refreshServerSide}
        onDrawerClose={() => setTemplateToDuplicate(null)}
      />
      {!!templateToManageCollaborators && (
        <TemplateCollaboratorsDialog
          templateId={templateToManageCollaborators.id}
          campaignId={
            isImportHubV2
              ? null
              : (templateToManageCollaborators as TemplateListItemV1).campaignId
          }
          mediaPlanId={
            isImportHubV2
              ? (templateToManageCollaborators as TemplateListItemV2)
                  .mediaPlanId
              : null
          }
          onSuccess={refreshServerSide}
          onClose={() => setTemplateToManageCollaborators(null)}
        />
      )}
    </>
  );
};

const MemoizedAgGrid = memo(
  ({
    ref,
    ...props
  }: AGGridProps & { ref: Ref<AgGridReact<TemplateListItem>> }) => {
    const { t } = useTranslation('@myn/import-hub');

    return (
      <AgGridReact
        ref={ref}
        className="[&_.ag-root.ag-unselectable]:select-auto! [&_.ag-tool-panel-wrapper]:min-w-[290px]!"
        loading={false}
        suppressCellFocus
        loadThemeGoogleFonts
        autoSizeStrategy={{ type: 'fitGridWidth' }}
        rowModelType="serverSide"
        serverSideInitialRowCount={PLACEHOLDER_ROWS_COUNT}
        suppressServerSideFullWidthLoadingRow={true}
        getRowId={(params) => params.data.id}
        domLayout="normal"
        sideBar={{
          toolPanels: ['filters'],
          position: 'right',
        }}
        getLocaleText={(params) => {
          if (
            params.key === 'searchOoo' ||
            params.key === 'ariaSearchFilterText'
          ) {
            return t('Search');
          }

          return params.defaultValue;
        }}
        noRowsOverlayComponent={GridEmptyState}
        {...props}
      />
    );
  },
  // The grid is driven imperatively through `gridRef` (filter model, page size,
  // server-side refreshes), so it deliberately ignores prop churn from the
  // parent instead of re-initialising on every render. `columnDefs` is the one
  // exception: it carries `cellRendererParams` that arrive after the first
  // render (e.g. `platformsByName` once the platforms query resolves), and
  // AG Grid only applies prop changes when this component actually re-renders.
  (prevProps, nextProps) => prevProps.columnDefs === nextProps.columnDefs,
);

const TemplateNameRenderer = ({
  t,
  data,
  value,
  handlePreviewTemplateClick,
}: CustomCellRendererProps<TemplateListItem> & {
  t: TFunction;
  handlePreviewTemplateClick: (template: TemplateListItem) => void;
}) => {
  if (!data || !value) {
    return null;
  }

  return (
    <Button
      variant="plain"
      className="max-w-full"
      aria-label={t('Show template preview')}
      data-dd-action-name="Show template preview"
      onClick={() => handlePreviewTemplateClick(data)}
    >
      <TruncateText>{value}</TruncateText>
    </Button>
  );
};

const EmailRenderer = ({
  t,
  data,
  copyToClipboard,
  copiedEmail,
  isReadOnlyUser,
}: CustomCellRendererProps<TemplateListItem> & {
  t: TFunction;
  copyToClipboard: (text: string) => Promise<void>;
  copiedEmail: string | null;
  isReadOnlyUser: boolean;
}) => {
  if (!data?.email) {
    return null;
  }

  const { email } = data;

  return (
    <div className="flex max-w-full items-center gap-2">
      {!isReadOnlyUser && (
        <Tooltip
          asChild
          description={
            email === copiedEmail ? t('Mail copied!') : t('Copy mail')
          }
        >
          <Button
            variant="plain"
            aria-label={t('Copy mail')}
            data-dd-action-name="Copy mail"
            onClick={() => copyToClipboard(email)}
          >
            <Copy />
          </Button>
        </Tooltip>
      )}
      <TruncateText tooltipClassName="max-w-none">{email}</TruncateText>
    </div>
  );
};

const UnifiedPlatformRenderer = ({
  data,
  platformsByName,
}: CustomCellRendererProps<TemplateListItem> & {
  platformsByName: PlatformsByName;
}) => {
  if (!data?.platform) {
    return '–';
  }

  const platformCatalogItem = platformsByName[data.platform];

  if (!platformCatalogItem) {
    return '–';
  }

  return <PlatformsList platforms={[platformCatalogItem]} />;
};

const GranularityLevelRenderer = ({
  data,
  granularityLevelLabelByType,
}: CustomCellRendererProps<TemplateListItem> & {
  granularityLevelLabelByType: Record<string, string>;
}) => {
  if (!data?.granularityLevel) {
    return '-';
  }

  return granularityLevelLabelByType[data.granularityLevel] || '-';
};

const CreationDateRenderer = ({
  t,
  data,
}: CustomCellRendererProps<TemplateListItem> & {
  t: TFunction;
}) => {
  if (!data) {
    return null;
  }

  const { created } = data;

  return (
    <div className="flex w-full items-center justify-between">
      <StaticDateSpan date={created} />
      {checkIsNewItem(created) && (
        <span
          className={cn(
            typographyVariants({ variant: 'caption1' }),
            'px-2 text-text-secondary uppercase',
          )}
        >
          {t('New')}
        </span>
      )}
    </div>
  );
};

const PlainTextRenderer = ({
  fieldName,
  data,
}: CustomCellRendererProps<TemplateListItem> & { fieldName: string }) => {
  const value = data?.[fieldName as keyof typeof data] as string;
  if (!value) {
    return null;
  }

  return <TruncateText>{value}</TruncateText>;
};

const CollaboratorsRenderer = ({
  data,
}: CustomCellRendererProps<TemplateListItem>) => {
  if (!data?.collaborators) {
    return null;
  }

  return <CollaboratorsList collaborators={data.collaborators} />;
};

const ActionsRenderer = ({
  data,
  handleManageCollaboratorsClick,
  handleDuplicateTemplateClick,
  handleDeleteTemplateClick,
}: CustomCellRendererProps<TemplateListItem> & {
  handleManageCollaboratorsClick: (template: TemplateListItem) => void;
  handleDuplicateTemplateClick: (template: TemplateListItem) => void;
  handleDeleteTemplateClick: (template: TemplateListItem) => void;
}) => {
  if (!data) {
    return null;
  }

  return (
    <GridTemplateMoreActions
      templateId={data.id}
      onCollaboratorsClick={() => handleManageCollaboratorsClick(data)}
      onDuplicateClick={() => handleDuplicateTemplateClick(data)}
      onDeleteClick={() => handleDeleteTemplateClick(data)}
    />
  );
};
